/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *	2022-08-17
 *		- create hotel-rooms.store.js to facilitate data storage between hotel-related components.
 *
 *	2022-08-19
 *		- Refactor getSelectedFilters() to the helper js directory.
 *
 *	2023-03-30
 *		- Refactor hotel-rooms.store.js to hotel-rooms.store.ts and add types to all function arguments.
 *
 *	2024-06-10 (jhoward)
 *		- Add roomTypeTotalAvailable getter.
 *		- Add selectedDates getter.
 *		- Add bookingLink getter.
 */

import {HotelRoomAdjacentDate, HotelRoomState, StateFilterSelected} from "./hotel-rooms";

//@ts-ignore
import Vue from 'vue/dist/vue.esm'
import Vuex from "vuex";
import {getSelectedFilters, SelectedFilterOutput} from "../helpers/get-selected-filters";
import {HotelBookingLinkData, RoomAvailabilityData, RoomAvailabilityRoomType} from "../components/hotel/defs";

Vue.use(Vuex);

export default new Vuex.Store<HotelRoomState>({
  state: {
    ROOMS: [] as RoomAvailabilityData[],
    ADJACENT_DATES: [] as HotelRoomAdjacentDate[],
    isRoomAvailabilityLoaded: false,
    filterSaveTimer: -1,
    filterSaveTimeout: 500,
    filters: [
      {
        title: 'Bed',
        key: 'bed_type',
        items: [
          {
            title: '1 King',
            value: '1-KING',
            selected: false
          },
          {
            title: '2 Kings',
            value: '2-KING',
            selected: false
          },
          {
            title: '2 Queens',
            value: '2-QUEEN',
            selected: false
          },
        ]
      },

      {
        title: 'Price per Night',
        key: 'price',
        items: [
          {
            title: 'Below $100',
            value: '0-100',
            selected: false
          },
          {
            title: '$100 to $200',
            value: '100-200',
            selected: false
          },
          {
            title: '$200 to $300',
            value: '200-300',
            selected: false
          },
          {
            title: '$300 or more',
            value: '300-10000',
            selected: false
          },
        ]
      },

      {
        title: 'Smoking',
        key: 'smoking',
        items: [
          {
            title: 'Smoking',
            value: 'YES',
            selected: false
          },
          {
            title: 'Non-Smoking',
            value: 'NO',
            selected: false
          },
        ]
      },

      {
        title: 'Accessibility',
        key: 'accessibility',
        items: [
          {
            title: 'Hearing Impaired',
            value: 'hearing impaired',
            selected: false
          },
          {
            title: 'Roll-In Shower',
            value: 'roll-in shower',
            selected: false
          },
          {
            title: 'Bathtub with Bars',
            value: 'bathtub with bars',
            selected: false
          },
        ]
      },

      {
        title: 'Room Size',
        key: 'sq_ft',
        items: [
          {
            title: 'Small',
            value: '100-400',
            selected: false
          },
          {
            title: 'Medium',
            value: '400-750',
            selected: false
          },
          {
            title: 'Large',
            value: '750-10000',
            selected: false
          },
        ]
      },
    ],

    //compatability with view-room-details for calendar component
    bookingLinkData: {
      start: null,
      end: null,
      adults: 2,
      children: 0,
    } as HotelBookingLinkData,

    checkInDate: null,
    checkOutDate: null,
  },
  mutations: {
    updateState: (state: HotelRoomState, data: [string, unknown]) => {
      if (data.length === 2 && data[0] in state) {
        //@ts-ignore
        state[data[0]] = data[1];
      }
    },
    updateRooms: (state: HotelRoomState, data: RoomAvailabilityData[]) => {
      if (Array.isArray(data)) {
        state.ROOMS = data;
      }
    },
    updateAdjacentDates: (state: HotelRoomState, data: HotelRoomAdjacentDate[]) => {
      if (Array.isArray(data)) {
        state.ADJACENT_DATES = data;
      }
    },
    updateFilter: (state: HotelRoomState, data: StateFilterSelected) => {
      let saveChange = false;
      state.filters.forEach((filterType, filterIndex) => {
        if (filterType.key === data.key) {
          filterType.items.forEach((filterValue, filterValueIndex) => {
            if (filterValue.value === data.value && state.filters[filterIndex].items[filterValueIndex].selected !== data.selected) {
              saveChange = true;
              state.filters[filterIndex].items[filterValueIndex].selected = data.selected;
            }
          });
        }
      });

      //persist filter change if any occurred
      if (saveChange) {
        clearTimeout(state.filterSaveTimer);

        state.filterSaveTimer = window.setTimeout(() => {
          window.pepHelperFunctions.setCachedData(
            'hotel-filter-widget-selections',
            {
              filters: getSelectedFilters(state.filters)
            }
          );
        }, state.filterSaveTimeout);
      }
    },
    updateCheckInDate: (state: HotelRoomState, checkInDate: Date | null) => {
      state.bookingLinkData.start = checkInDate;
      state.checkInDate = checkInDate;
    },
    updateCheckOutDate: (state: HotelRoomState, checkOutDate: Date | null) => {
      state.bookingLinkData.end = checkOutDate;
      state.checkOutDate = checkOutDate;
    }
  },
  getters: {
    currentRoomWingCode: (state: HotelRoomState, getters) => {
      const currentRoomType = getters.currentRoomType;
      if (currentRoomType && 'DATA' in currentRoomType && currentRoomType.DATA.length && 'rrcsn_wing_code' in currentRoomType.DATA[0]) {
        return currentRoomType.DATA[0].rrcsn_wing_code;
      }
      return '';
    },

    selectedFilters: function (state: HotelRoomState): SelectedFilterOutput {
      return getSelectedFilters(state.filters);
    },

    //compatability with view-room-details for calendar component
    bookingLinkRoomPageId: () => 0,

    stayLength: function (state: HotelRoomState) {
      if (state.checkInDate && state.checkOutDate) {
        return window.hotelRoomsVueApp.daysBetween(state.checkInDate, state.checkOutDate);
      }

      return 0;
    },

    roomTypeIsAvailable: (state: HotelRoomState, getters) => (roomAvailabilityData: RoomAvailabilityData): boolean => {
      const stayLength = getters.stayLength;
      const validRoomTypes = roomAvailabilityData.ROOM_TYPES.filter(roomType => {
        return roomType.MIN_STAY_DAYS <= stayLength
      });
      return !!validRoomTypes.length;
    },

    roomTypeTotalAvailable: (state: HotelRoomState, getters) => (roomAvailabilityData: RoomAvailabilityData): number => {
      const stayLength = getters.stayLength;
      const validRoomTypes = roomAvailabilityData.ROOM_TYPES.filter(roomType => {
        return roomType.MIN_STAY_DAYS <= stayLength
      });
      return validRoomTypes.map(roomType => roomType.ROOM_INVENTORY).reduce((roomInventory, totalInventory) => roomInventory + totalInventory, 0);
    },

    roomTypeMinStayDays: (state: HotelRoomState, getters) => (roomAvailabilityData: RoomAvailabilityData): number => {
      if (false === getters.roomTypeIsAvailable(roomAvailabilityData)) {
        const minStayRoomTypes = roomAvailabilityData.ROOM_TYPES.filter(roomType => {
          return roomType.MIN_STAY_DAYS > getters.stayLength
        });
        if (minStayRoomTypes.length) {
          return minStayRoomTypes[0].MIN_STAY_DAYS;
        }
      }

      return 0;
    },

    selectedDates: (state: HotelRoomState): string[] => {
      const output = [];
      if (state.checkInDate && state.checkOutDate) {
        let currentDate = state.checkInDate;
        const checkOutTime = state.checkOutDate.getTime();
        while (currentDate.getTime() <= checkOutTime) {
          output.push(window.pepDateFunctions.formatDateObjectYYYYMMDD(currentDate));
          currentDate = window.pepDateFunctions.addDays(currentDate, 1);
        }
      } else if (state.checkInDate) {
        output.push(window.pepDateFunctions.formatDateObjectYYYYMMDD(state.checkInDate));
      } else if (state.checkOutDate) {
        output.push(window.pepDateFunctions.formatDateObjectYYYYMMDD(state.checkOutDate));
      }

      return output;
    },

    bookingLink: (state, getters) => (roomAvailabilityData: RoomAvailabilityData, prtId = 0, offerCode = '', pageId = 0, clickSource = 'hotel-widget'): string => {
      const params: {
        room_page_id: number,
        clickSource: string,
        o: string,
        start?: string,
        end?: string,
        test?: number,
        auto_book_prt?: number,
        room?: number
      } = {
        room_page_id: pageId,
        clickSource,
        o: offerCode
      };

      if (state.checkInDate) {
        params.start = window.pepDateFunctions.formatDateObjectYYYYMMDD(state.checkInDate);
      }
      if (state.checkOutDate) {
        params.end = window.pepDateFunctions.formatDateObjectYYYYMMDD(state.checkOutDate);
      }

      if (getters.roomTypeIsAvailable(roomAvailabilityData)) {
        let selectedRoomTypeIndex = 0;
        let isNonSmokingRoomTypeFound = false;

        if (/[&?]test=1$/.test(location.href)) {
          params.test = 1;
        }

        const availableRoomTypes: RoomAvailabilityRoomType[] = [];
        roomAvailabilityData.ROOM_TYPES.forEach((roomType: RoomAvailabilityRoomType) => {
          if ((roomType?.ROOM_INVENTORY || 0) > 0) {
            availableRoomTypes.push(roomType);
          }
        });

        if (prtId) {
          for (let i = 0, l = availableRoomTypes.length; i < l; i++) {
            if (availableRoomTypes[i].PRT_ID === prtId) {
              selectedRoomTypeIndex = i;
              break;
            }
          }
        } else {
          //let's default to non-smoking room type if we can
          for (let i = 0, l = availableRoomTypes.length; i < l; i++) {
            if (availableRoomTypes[i].hasOwnProperty('ROOM_NON_SMOKING') && availableRoomTypes[i].ROOM_NON_SMOKING) {
              if (availableRoomTypes[i].hasOwnProperty('PRT_ID')) {
                params.auto_book_prt = availableRoomTypes[i].PRT_ID;
              }

              if (availableRoomTypes[i].hasOwnProperty('ROOM_SEQUENCE_NUMBER')) {
                params.room = availableRoomTypes[i].ROOM_SEQUENCE_NUMBER;
              }

              isNonSmokingRoomTypeFound = true;
              selectedRoomTypeIndex = i;

              break;
            }
          }
        }

        if (!isNonSmokingRoomTypeFound && availableRoomTypes.hasOwnProperty(selectedRoomTypeIndex)) {
          if (availableRoomTypes[selectedRoomTypeIndex].hasOwnProperty('PRT_ID')) {
            params.auto_book_prt = availableRoomTypes[selectedRoomTypeIndex].PRT_ID;
          }

          if (availableRoomTypes[selectedRoomTypeIndex].hasOwnProperty('ROOM_SEQUENCE_NUMBER')) {
            params.room = availableRoomTypes[selectedRoomTypeIndex].ROOM_SEQUENCE_NUMBER;
          }
        }
      }

      return '/view-room-details/?' + $.param(params);
    }
  }
});